import { useState } from 'react';
import { useParams } from 'react-router';
import { CapabilityDetailLayout, WORKING_COPY_ID } from '@/components/capability-detail/CapabilityDetailLayout';
import { useEditorBundle, useBundleRoot } from '@/hooks/use-editor-bundle';
import { Badge } from '@/components/ui/badge';
import { Shield } from 'lucide-react';

export default function PermissionDetailPage() {
    const { appKey, name } = useParams();
    const [selectedRevisionId, setSelectedRevisionId] = useState(WORKING_COPY_ID);
    const { data: bundle, isLoading } = useEditorBundle(appKey!, name!);
    const data = useBundleRoot(bundle) as Record<string, unknown> | null;

    if (isLoading) return <div className="p-6 text-muted-foreground">Loading permission...</div>;

    const parts = (name ?? '').split(':');
    const namespace = parts[0] ?? '';
    const resource = parts[1] ?? '';
    const action = parts[2] ?? '';

    return (
        <CapabilityDetailLayout
            typeLabel="Permission"
            appKey={appKey}
            capabilityName={name}
            entityData={data as Record<string, unknown> | undefined}
            selectedRevisionId={selectedRevisionId}
            onRevisionChange={setSelectedRevisionId}
        >
            <div className="p-6 space-y-6">
                <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-100">
                        <Shield className="h-6 w-6 text-violet-600" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold">{name}</h2>
                        <p className="text-sm text-muted-foreground">
                            {(data as any)?.description ?? 'No description'}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                    <div className="rounded-lg border p-4">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Namespace</p>
                        <Badge variant="outline" className="mt-1">{namespace}</Badge>
                    </div>
                    <div className="rounded-lg border p-4">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Resource</p>
                        <Badge variant="outline" className="mt-1">{resource}</Badge>
                    </div>
                    <div className="rounded-lg border p-4">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Action</p>
                        <Badge variant="outline" className="mt-1">{action}</Badge>
                    </div>
                </div>

                <div className="rounded-lg border p-4">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Visibility</p>
                    <Badge>{(data as any)?.visibility ?? 'global'}</Badge>
                </div>
            </div>
        </CapabilityDetailLayout>
    );
}
